import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { selectZipCode } from '../reducers/zipCodeReducer';
import { IS_TIER_1, PARTNER_ID } from '../constants/environment';
import { PromotionType } from '../types/offers';
import { CARLINES } from '../constants/ModelFamilyList';

const useCtaUrls = (offer: PromotionType) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const { confirmedZipcode } = useSelector(selectZipCode);

  const { bodyType, modelYear, modelFamily, drivetrain } = offer.offerModelDetail;

  const isQ5tfsieModel = offer.offerModelDetail.modelFamily === 'Q5e';
  const isQ4model = offer.offerModelDetail.modelFamily === 'Q4 e-tron';

  // Modify specific model name for VLP filters
  const correctModelFamily = useMemo(() => {
    if (isQ5tfsieModel) return 'q5tfsie';
    if (isQ4model) return 'q4';
    return modelFamily.replace(/[ -]/g, '').toLocaleLowerCase();
  }, [isQ4model, isQ5tfsieModel, modelFamily]);

  // Modify specific body type name for VLP filters
  const correctBodyStyle =
    bodyType === 'Sedan' ? 'limousine' : bodyType.replace(/[ -]/g, '_').toLowerCase();

  // Parsing filter params for VL
  // -----------------------------------------------------------------------------------------
  const modelFamilyParams = correctModelFamily ? `modelFamily=${correctModelFamily}` : '';
  const carlinesParams = CARLINES[correctModelFamily]?.[bodyType]
    ? `carline=${CARLINES[correctModelFamily]?.[bodyType]}`
    : '';
  const bodyTypeParams = correctBodyStyle ? `&body-style=${correctBodyStyle}` : '';
  const driveTrainParams = drivetrain ? `&drivetrain=${drivetrain}` : '';
  const yearParams = modelYear ? `&year=${modelYear}:${modelYear}` : '';

  const partnerId = PARTNER_ID ? `&partner_id=${PARTNER_ID}` : '';
  const zipCode = confirmedZipcode ? `&geoZip=${confirmedZipcode}` : '';
  // -----------------------------------------------------------------------------------------

  // Combining feilter params for T1 and T1 VLP
  const tier1Params = useMemo(() => {
    return `${carlinesParams}${driveTrainParams}${yearParams}${zipCode}${partnerId}`;
  }, [carlinesParams, driveTrainParams, partnerId, yearParams, zipCode]);

  const tier3Params = useMemo(() => {
    return `${modelFamilyParams}${bodyTypeParams}${driveTrainParams}${yearParams}`;
  }, [bodyTypeParams, driveTrainParams, modelFamilyParams, yearParams]);

  const urlHost = window.location.origin;

  const inventoryUrlTier1 = `${urlHost}/${lang}/inventory/?${tier1Params}`;
  const inventoryUrlTier3 = `${urlHost}/${lang}/inventory/new/#${tier3Params}`;

  const inventoryUrl = IS_TIER_1 ? inventoryUrlTier1 : inventoryUrlTier3;
  return inventoryUrl;
};

export default useCtaUrls;
